<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, shallowRef, watch} from "vue";
import type {ICellRendererParams} from "ag-grid-community";
import {echarts} from "@/utils/common/echartsSetup.ts";

type SparklineOptions = {
  type?: 'line' | 'area' | 'column';
  lineColor?: string;
  fill?: string;
};

const props = defineProps<{
  params: ICellRendererParams & {
    rendererOptions?: { sparkline?: SparklineOptions };
    colId?: string;
  };
}>();

const container = ref<HTMLDivElement | null>(null);
const chart = shallowRef<echarts.ECharts | null>(null);
let resizeObserver: ResizeObserver | null = null;

function resolveColId(): string | undefined {
  return props.params.colId ?? props.params.column?.getColId();
}

const seriesData = computed<number[]>(() => {
  const data: any = props.params.data;
  const colId = resolveColId();
  const arr = data?.cells?.[colId ?? '']?.sparklineData;
  return Array.isArray(arr) ? arr : [];
});

const sparkOpts = computed<SparklineOptions>(() => props.params.rendererOptions?.sparkline ?? {});

function buildOption() {
  const opts = sparkOpts.value;
  const lineColor = opts.lineColor ?? '#2196f3';
  const fillColor = opts.fill ?? (opts.type === 'area' ? lineColor : undefined);
  const isColumn = opts.type === 'column';
  const data = seriesData.value;
  return {
    animation: false,
    grid: {top: 2, right: 2, bottom: 2, left: 2, containLabel: false},
    xAxis: {type: 'category', show: false, data: data.map((_, i) => i)},
    yAxis: {type: 'value', show: false, scale: true},
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: {type: isColumn ? 'shadow' : 'line'},
      formatter: (params: any[]) => {
        const p = Array.isArray(params) ? params[0] : params;
        return String(p.value);
      },
    },
    series: [{
      type: isColumn ? 'bar' : 'line',
      data,
      symbol: 'none',
      smooth: false,
      lineStyle: {color: lineColor, width: 1.5},
      itemStyle: {color: fillColor ?? lineColor},
      areaStyle: opts.type === 'area' ? {color: fillColor ?? lineColor, opacity: 0.3} : undefined,
      barWidth: '70%',
    }],
  };
}

function ensureChart() {
  if (!container.value) return;
  const w = container.value.clientWidth;
  const h = container.value.clientHeight;
  if (w === 0 || h === 0) {
    requestAnimationFrame(ensureChart);
    return;
  }
  if (!chart.value) chart.value = echarts.init(container.value, undefined, {renderer: 'canvas'});
  chart.value.setOption(buildOption(), true);
}

onMounted(() => {
  requestAnimationFrame(ensureChart);
  if (container.value && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(() => chart.value?.resize());
    resizeObserver.observe(container.value);
  }
});

watch(seriesData, () => ensureChart());
watch(sparkOpts, () => ensureChart(), {deep: true});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  chart.value?.dispose();
  chart.value = null;
});
</script>

<template>
  <div ref="container" style="width: 100%; height: 36px; display: block;"></div>
</template>
